import { Trans } from "@lingui/macro";
import { IPeriodUnit } from "@smp/api";
import { UiBox, UiTypography } from "@smp/ui";
import { testAppendAttr, textCurrencySymbolGet } from "@smp/utils";
import { FC, PropsWithChildren } from "react";

import { IInvoiceFormValues } from "../../use_invoice_form";
import { CompositionFeature } from "./components/composition_feature";
import { CompositionPlanProduct } from "./components/composition_plan_product";
import { CompositionPostsPackage } from "./components/composition_posts_package";
import { Details } from "./components/details";
import { PlanProductAdditions } from "./components/plan_product_additions";
import classes from "./product_description.module.css";

export interface IProductDescriptionProps {
  product: IInvoiceFormValues["product"];
  paymentType: IInvoiceFormValues["paymentType"];
  monthlyPrice: number | undefined;
  productName?: string;
}

export const ProductDescription: FC<
  PropsWithChildren<IProductDescriptionProps>
> = ({ product, paymentType, monthlyPrice, children, productName }) => {
  const isOneYearPeriodPlanProduct =
    product.type === "plan-product" &&
    product.period_length === 1 &&
    product.period_units === IPeriodUnit.YEAR;

  return (
    <div
      className={classes.container}
      {...testAppendAttr("id", `purchase_details`)}
    >
      <UiBox display="flex">
        <UiTypography variant="h5">
          <Trans>Payment details</Trans>
        </UiTypography>

        {isOneYearPeriodPlanProduct && (
          <UiTypography className={classes.discount} noWrap>
            <Trans>
              __dialog_purchase.prepare.details__Pay Yearly & Save up to {33}%
            </Trans>
          </UiTypography>
        )}
      </UiBox>

      <div className={classes.divider} />

      <Details
        product={product}
        isOneYearPeriodPlanProduct={isOneYearPeriodPlanProduct}
        monthlyPrice={monthlyPrice}
        productName={productName}
      />

      {product.type === "posts-package" && (
        <CompositionPostsPackage product={product} />
      )}
      {product.type === "plan-product" && (
        <CompositionPlanProduct product={product} />
      )}
      {product.type === "feature-subscription" && (
        <CompositionFeature product={product} />
      )}

      <div className={classes.divider} />

      <UiBox display="flex" flexDirection="row" mb={2}>
        <UiBox flexGrow={1}>
          <UiTypography variant="h5">
            <Trans>To payment:</Trans>
          </UiTypography>
        </UiBox>
        <UiBox display="flex" flexGrow={0} textAlign="right">
          {isOneYearPeriodPlanProduct && monthlyPrice && (
            <UiTypography
              className={classes.discount_price_text}
              color="textSecondary"
              noWrap
              variant="h5"
              mr={1}
            >
              <s>{(monthlyPrice * 12).toFixed(0)}</s>
            </UiTypography>
          )}
          <UiTypography variant="h5">
            {product.price} {textCurrencySymbolGet(product.currency)}
          </UiTypography>
        </UiBox>
      </UiBox>

      {product.type === "plan-product" && (
        <PlanProductAdditions product={product} paymentType={paymentType} />
      )}

      {children}
    </div>
  );
};
